import * as THREE from "three";
import BasePlate from "./BasePlate";
import Hotspot from "./hotspots/Hotspot";
import PinnedVideo from "./PinnedVideo";
import * as TWEEN from "@tweenjs/tween.js";
import { StereoEffect } from "./utils/StereoEffect";
import PinnedVideoNode from "./hotspots/PinnedVideoNode";

import eventTours from "./IframeControl";
import GlobalEvent from "js-events-listener";

let _activePopupListiner;

/**
 * Represents a 360 scene within the player
 */
export default class Scene extends THREE.Scene {
  fullyLoaded;
  onFullyLoaded;
  prefetchTimeout;
  isSplashScreen;
  aspectRatio;
  _photoHolder;
  fov;

  /**
   * Takes an image config object and creates the initial 360 scene
   *
   * @example
   * {
   *   url: '/example.jpg',
   *   hotspots: [
   *     {
   *       u: 0,
   *       v: 0.5,
   *       type: 'web',
   *       options: {
   *         url : 'http://google.com'
   *       }
   *     }
   *   ]
   * }
   *
   */
  constructor(
    player,
    image,
    initialFunction = () => { },
    onFullyLoaded = () => { },
    sceneName = "Scene360"
  ) {
    super();
    this.SCENE_NAME = sceneName;
    this._image = image;
    this.aspectRatio = image.width / image.height;

    this.fov = image.fov ?? null;

    // Load photosphere
    player.loadingManager.hide();
    this.imageLoaded = false;
    this.fullyLoaded = false;
    this.isLookingAt = false;
    this.isSplashScreen = player?.isSplashScreen?.();
    this.group = new THREE.Group();
    this.latestMouse = { x: 0, y: 0 };
    this.onFullyLoaded = (callback = () => { }) => {
      this.fullyLoaded = true;
      onFullyLoaded();
      callback();
      this.checkPrefetch(250);
    };

    this._player = player;
    this.add(this._player.camera);

    initialFunction(this);

    // for hiding hotspots when a lightbox is open
    player.disableHotspots = false;

    this._hotspots = [];
    for (const h in player.getHotspotsFromImage(image.id)) {
      const hotspot = this.hotspotFromConfig(
        player,
        player.getHotspotsFromImage(image.id)[h],
      );
      this.addHotspot(hotspot);
    }

    if (player.viewerMode === "device") {
      this._player.camera.remove(this._player.crosshair);
      this._player.crosshair.position.z = -2;
      this._player.camera.add(this._player.crosshair);
    }

    this.pinnedVideoMarkers = [];

    this.getImage = false;
    this.imageName = "export";
    this.showNodes = true;
    this.isShowContextMenu = false;
    const renderTargetParams = {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBFormat,
      stencilBuffer: false,
    };
    /**
     * FBO for this scene
     */
    this.fbo = new THREE.WebGLRenderTarget(
      window.innerWidth,
      window.innerHeight,
      renderTargetParams,
    );

    this.effect = new StereoEffect(this._player.renderer);

    this._basePlate = this.createBaseplate(this._player.viewerMode);
    this.add(this._basePlate);

    this._pinnedVideos = [];
    if (image.pinnedVideos !== undefined) {
      for (const v of image.pinnedVideos) {
        const video = new PinnedVideo(v);
        this.addPinnedVideo(video);
      }
    }

    this._startingPoint = undefined;
    if ((image.startingPoint ?? undefined) !== undefined) {
      this._startingPoint = image.startingPoint;
    }
    this.add(this.group);

    if (this._player.renderer?.xr?.enabled === true) {
      this.add(this._player.camera);
      this._player.camera.add(this._player.crosshair);
      this._player.crosshair.position.z = -2;
    }

    if (_activePopupListiner)
      GlobalEvent.removeEventListener(_activePopupListiner);

    _activePopupListiner = GlobalEvent.on("INNER_SET_ACTIVE_POPUP", data => {
      eventTours.lockControl = false;
      const { uuid } = data;
      const findHotspot = this._hotspots.find(val => val._config.uuid === uuid);
      if (findHotspot) {
        this.setActivePopup(findHotspot, true);
      }
      eventTours.lockControl = true;
    });
  }

  /**
   *
   * @param uv
   */
  addPinnedVideoMarker(uv) {
    let marker = new PinnedVideoNode(this._player, { u: uv.x, v: uv.y });
    this.pinnedVideoMarkers.push(marker);

    if (this.pinnedVideoMarkers?.length >= 4) {
      this.pinnedVideoMarkers.forEach(m => {
        this.group.remove(m);
      });
      this.pinnedVideoMarkers = [];
    } else {
      this.group.add(marker);
    }
  }

  /**
   *
   * @param precision
   * @returns {*}
   */
  getAspectRatio(precision = 1) {
    return this.aspectRatio.toFixed(precision);
  }

  /**
   *
   * @returns {*}
   */
  getSceneFov() {
    return this.fov;
  }

  /**
   *
   * @returns {*}
   */
  getTargetFov() {
    let scene_fov = this.getSceneFov();
    let target_fov = null;

    if (scene_fov) {
      target_fov = this._player.isThumbnail ? scene_fov / 2 : scene_fov;
    } else {
      // eslint-disable-next-line eqeqeq
      if (this.getAspectRatio() == 2) {
        target_fov = this._player.isThumbnail
          ? this._player.THUMBNAIL_CAMERA_FOV
          : this._player.CAMERA_FOV;
      } else {
        target_fov = this._player.PARTIAL_FOV;
      }
    }

    return target_fov;
  }

  /**
   * Update function to run every frame.

   * This keeps the orientation of the baseplate static.
   *
   * @param {THREE.Camera} camera
   */
  update() {
    if (this._basePlate && this._player) {
      const camera = this._player.getActiveCamera();
      this._basePlate.update(camera.rotation.z);
    }
  }

  enterVR() {
    this._player.renderer.xr.enabled = true;
    this.add(this._player.camera);
    this._player.camera.add(this._player.crosshair);
    this._player.crosshair.position.z = -2;
    this._player.viewerMode = "3D";
  }

  exitVR() {
    this._player.renderer.xr.enabled = false;
    this._player.camera.remove(this._player.crosshair);
    this._player.setControls("orbit");
  }

  createBaseplate() {
    let basePlate;
    basePlate = new BasePlate(this._player, 0, false);
    basePlate.rotation.x = -Math.PI / 2;
    basePlate.position.y = -80;
    return basePlate;
  }

  /**
   * Adds hotspot object to the scene and list
   *
   * @param {BaseHotspot} hotspot
   */
  addHotspot(hotspot) {
    this._hotspots.push(hotspot);
    this.group.add(hotspot);
    if (this._player) {
      // update tooltips
      this._player.findTooltips();
    }
  }

  /**
   *
   * @param config
   */
  addHotspotFromConfig(config) {
    this.addHotspot(this.hotspotFromConfig(this._player, config));
  }

  /**
   *
   * @param player
   * @param config
   * @returns {BaseHotspot}
   */
  hotspotFromConfig(player, config) {
    let _hsOptions = this._photoHolder
      ? this._photoHolder.getHotspotOptions(this.SCENE_NAME === "Scene3D")
      : null;

    return Hotspot.fromConfig(player, config, _hsOptions);
  }

  getRelatedImageIds() {
    let ids = [];
    for (const h in this._hotspots) {
      if (this._hotspots[h]._config.options.id ?? false) {
        ids.push(this._hotspots[h]._config.options.id);
      }
    }

    return ids;
  }

  /**
   *
   * @param hotspot
   */
  updateHotspot(hotspot) {
    let found = false;
    for (let i = 0; i < this._hotspots?.length; i++) {
      if (this._hotspots[i].getUUID() === hotspot.getUUID()) {
        this._hotspots[i].updateTooltip(hotspot._config.tooltip);
        this._hotspots[i]._config = hotspot._config;
        this._hotspots[i].move();
        this._hotspots[i].updateIcon();
        // Update scale if the hotspot has an updateScale method (for sprites/stickers)
        if (typeof this._hotspots[i].updateScale === 'function') {
          this._hotspots[i].updateScale();
        }
        found = true;
        break;
      }
    }
    if (!found) {
      console.warn("Could not find hotspot to update");
    }
    // update tooltips
    this._player.findTooltips();
  }

  /**
   * Add pinned video
   *
   * @param {PinnedVideo} video
   */
  addPinnedVideo(video) {
    this._pinnedVideos.push(video);
    this.group.add(video);
  }

  /**
   * Set active popup for React UI
   *
   * @param {Hotspot} hotspot
   */
  setActivePopup(hotspot, fromEvent = false) {
    if (hotspot._config?.type !== "poi") {
      if (eventTours.lockControl) return;
      if (!fromEvent) GlobalEvent.emit("SET_ACTIVE_POPUP", hotspot);
      // show previously selected hotspot
      if (this._player.activePopup) this._player.activePopup.setInactive();
      hotspot.setActive();
      this._player.centeredPopup = false;
      this._player.activePopup = hotspot;
      this._player.onPopupOpen(hotspot._config);
    }
  }

  /**
   * Gets intersects from the photo sphere
   */
  intersectWithPhotoHolder(mouse, camera) {
    const raycaster = new THREE.Raycaster();
    if (
      (this._player.renderer.xr && this._player.renderer.xr.enabled === true) ||
      this._player.viewerMode === "device"
    ) {
      raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    } else if (this._player.viewerMode === "stereo") {
      raycaster.setFromCamera({ x: 0, y: 0 }, this.effect.cameraL());
    } else {
      raycaster.setFromCamera(mouse, camera);
    }

    let _sp = this._photoHolder.projectSphere();

    if (_sp) {
      this.group.add(_sp);
      _sp.updateMatrixWorld(true);
      let x = raycaster.intersectObject(_sp);
      this.group.remove(_sp);
      return x;
    }

    return raycaster.intersectObject(this._photoHolder);
  }

  onClearHotspotHover() {
    for (let h = 0; h < this._hotspots?.length; h++) {
      this._hotspots[h].setHover(false);
    }
    if (this._otherScene) {
      for (let h = 0; h < this._otherScene._hotspots?.length; h++) {
        this._otherScene._hotspots[h].setHover(false);
      }
    }
  }

  /**
   * Triggered by the player to update hover effects of hotspots
   *
   * @param {MouseEvent} event
   * @param {THREE.Vector2} mouse - mouse position as a Vector2
   * @param {THREE.Camera} camera
   */
  onMouseMove(event, mouse, camera) {
    if (
      (mouse.x + 1) * 50 > this._player.divider &&
      this.split &&
      !this.isOther
    ) {
      for (let h = 0; h < this._hotspots?.length; h++) {
        this._hotspots[h].setHover(false);
      }
      this._otherScene.onMouseMove(event, mouse, camera);
      return;
    } else {
      if (this._otherScene) {
        for (let h = 0; h < this._otherScene._hotspots?.length; h++) {
          this._otherScene._hotspots[h].setHover(false);
        }
      }
    }
    // Set all the hotspots to be non-hovered
    for (let h = 0; h < this._hotspots?.length; h++) {
      this._hotspots[h].setHover(false);
      if (this._player.viewerMode !== "stereo") {
        this._hotspots[h].onMouseMove(event);
      }
    }

    // set the latest mouse for calculating hotspot sizes
    this.latestMouse = {
      ...mouse,
    };

    // find hotspots via raycast
    const hotspots = this.hotspotsUnderMouse(mouse, camera);

    // If in either VR or device mode allow users to hover over nodes to select them
    if (
      this._player.viewerMode === "stereo" ||
      (this._player.renderer.xr && this._player.renderer.xr.enabled === true) ||
      this._player.viewerMode === "device"
    ) {
      if (!this.currentlyHoveredHotspot && hotspots?.length > 0) {
        this._hotspotHoverTimeout = setTimeout(() => {
          // Function run when the hotspot has been hovered over for
          // a certain time
          if (
            this._player.popupOverride &&
            !["link", "fade", "elevate-up", "elevate-down"].includes(
              this.currentlyHoveredHotspot._config?.type,
            )
          ) {
            this.setActivePopup(this.currentlyHoveredHotspot);
          } else {
            this.currentlyHoveredHotspot.action();
          }
          this.currentlyHoveredHotspot = false;
        }, 1500);
        this.currentlyHoveredHotspot = hotspots[0];
      } else {
        let found = false;
        for (let i in hotspots) {
          if (
            this.currentlyHoveredHotspot &&
            hotspots[i] &&
            (hotspots[i].uuid === this.currentlyHoveredHotspot.uuid || 
              hotspots[i]._config?.uuid === this.currentlyHoveredHotspot._config?.uuid)
          ) {
            found = true;
          }
        }
        if (found === false) {
          clearTimeout(this._hotspotHoverTimeout);
          this.currentlyHoveredHotspot = false;
        }
      }
    }

    for (let i = 0; i < hotspots?.length; i++) {
      hotspots[i].setHover(true && !this.isShowContextMenu);
    }
  }

  /**
   * Triggered by the player on mouse up.
   * Handles whether to trigger hotspot actions
   *
   * @todo Hotspot actions shouldn't be triggered if click wasn't on canvas
   *
   * @param {MouseEvent} event
   * @param {THREE.Vector2} mouse - mouse position as a Vector2
   * @param {THREE.Camera} camera
   *
   */
  onMouseUp(event, mouse, camera) {
    if (this._otherScene) this._otherScene.onMouseUp(event, mouse, camera);

    if (event.changedTouches) {
      mouse = {};
      mouse.x =
        ((event.changedTouches[0].clientX - this._player.container.offsetLeft) /
          this._player.renderer.domElement.clientWidth) *
        2 -
        1;
      mouse.y =
        -(
          ((event.changedTouches[0].clientY -
            this._player.container.offsetTop) /
            this._player.renderer.domElement.clientHeight) *
          2
        ) + 1;
      // Hide all tooltips / deselect hotspots
      for (let i = 0; i < this._hotspots?.length; i++) {
        this._hotspots[i].setHover(false);
      }
    }
    // Get list of hotspots which have been clicked on
    const hotspots = this.hotspotsUnderMouse(mouse, camera);
    // New popup system can only handle 1 at a time, so take the first one
    if (this._player.popupOverride && hotspots?.length > 0) {
      if (
        ["link", "fade", "elevate-up", "elevate-down"].includes(
          hotspots[0]._config?.type,
        )
      ) {
        hotspots[0].action(event);
      } else {
        this.setActivePopup(hotspots[0]);
      }
    }

    // Get list of videos which have been clicked on and toggle play state
    const videos = this.pinnedVideosUnderMouse(mouse, camera);

    for (let j = 0; j < videos?.length; j++) {
      videos[j].toggle();
    }
    this.dragging = false;
  }

  /**
   *
   * @param event
   * @param mouse
   * @param camera
   */
  onMouseDown(event, mouse, camera) {
    if (this._otherScene) this._otherScene.onMouseDown(event, mouse, camera);

    if (
      (this._player.viewerMode === "stereo" ||
        this._player.viewerMode === "device") &&
      !this.isThumbnail &&
      this._player._options.getUIVisibility &&
      !this._player._options.getUIVisibility()
    ) {
      clearTimeout(this._player._clickToShowMenuTimeout);
      if (this._player._options.setUIVisibility) {
        this._player._options.setUIVisibility(true);
      }
      this._player._clickToShowMenuTimeout = setTimeout(() => {
        this._player._options.setUIVisibility(false);
      }, 3000);
    }
    if (this._player.isMobile && event.changedTouches) {
      mouse = {};
      mouse.x =
        ((event.changedTouches[0].clientX - this._player.container.offsetLeft) /
          this._player.renderer.domElement.clientWidth) *
        2 -
        1;
      mouse.y =
        -(
          ((event.changedTouches[0].clientY -
            this._player.container.offsetTop) /
            this._player.renderer.domElement.clientHeight) *
          2
        ) + 1;
    }
    for (let i = 0; i < this._hotspots?.length; i++) {
      this._hotspots[i].onMouseMove(event);
      this._hotspots[i].setHover(false);
    }
    // Get list of hotspots which have been clicked on
    const hotspots = this.hotspotsUnderMouse(mouse, camera);
    for (let i = 0; i < hotspots?.length; i++) {
      hotspots[i].onMouseMove(event);
      hotspots[i].setHover(true);
    }
  }

  /**
   *
   * @param event
   */
  onMouseDrag(event) {
    if (!this._hideTooltipTimeout) {
      this._hideTooltipTimeout = setTimeout(() => {
        for (let h = 0; h < this._hotspots?.length; h++) {
          this._hotspots[h].setHover(false);
        }
      }, 200);
    }

    this.dragging = true;
  }

  /**
   *
   * @param event
   */
  onMouseStopDrag(event) {
    clearTimeout(this._hideTooltipTimeout);
    this._hideTooltipTimeout = false;
    this.dragging = false;
  }

  /**
   *
   * @param value
   */
  setShowContextMenu(value) {
    this.isShowContextMenu = value;
  }

  /**
   * Gets a list of hotspots that are under the mouse
   *
   * @param {THREE.Vector2} mouse - mouse position as a Vector2
   * @param {THREE.Camera} camera
   * @return {Hotspot[]}
   */
  hotspotsUnderMouse(mouse, camera) {
    if (this._player.disableHotspots || !this._hotspots || this._hotspots.length === 0) {
      return [];
    }

    const raycaster = new THREE.Raycaster();
    if (
      (this._player.renderer.xr && this._player.renderer.xr.enabled === true) ||
      this._player.viewerMode === "device"
    ) {
      raycaster.setFromCamera({ x: 0, y: 0 }, camera);
    } else if (this._player.viewerMode === "stereo") {
      raycaster.setFromCamera({ x: 0, y: 0 }, this.effect.cameraL());
    } else {
      raycaster.setFromCamera(mouse, camera);
    }

    // old BaseHotspot handler
    // return raycaster
    // .intersectObjects(this._hotspots)
    // .map(intersection => intersection.object);
    try {
      const intersections = raycaster.intersectObjects(this._hotspots, true); // true = recursive, check children
      
      // Safety check: ensure intersections is an array
      if (!Array.isArray(intersections)) {
        return [];
      }
      
      // Simple approach: get unique hotspots from userData
      const uniqueHotspots = [];
      const seenHotspots = new Set();
      
      for (const intersection of intersections) {
        const hotspot = intersection.object.userData?.hotspot || intersection.object;
        
        if (hotspot && this._hotspots.includes(hotspot) && !seenHotspots.has(hotspot)) {
          seenHotspots.add(hotspot);
          uniqueHotspots.push(hotspot);
        }
      }
      
      return uniqueHotspots;
    } catch (error) {
      console.warn("Error in hotspotsUnderMouse:", error);
      return [];
    }
  }

  /**
   * Gets a list of videos that are under the mouse
   *
   * @param {THREE.Vector2} mouse - mouse position as a Vector2
   * @param {THREE.Camera} camera
   * @return {PinnedVideo[]}
   */
  pinnedVideosUnderMouse(mouse, camera) {
    const raycaster = new THREE.Raycaster();
    if (
      (this._player.renderer.xr && this._player.renderer.xr.enabled === true) ||
      this._player.viewerMode === "device"
    ) {
      raycaster.setFromCamera({ x: 0, y: 0 }, camera);
    } else if (this._player.viewerMode === "stereo") {
      raycaster.setFromCamera({ x: 0, y: 0 }, this.effect.cameraL());
    } else {
      raycaster.setFromCamera(mouse, camera);
    }

    return raycaster
      .intersectObjects(this._pinnedVideos)
      .map(intersection => intersection.object);
  }

  /**
   *
   * @param {String} viewerMode - sets the viewer mode e.g 'orbit','device','stereo'
   *
   */
  onControlsChange(viewerMode) {
    if (viewerMode === "device" || viewerMode === "stereo") {
      if (this._player._options.setUIVisibility)
        this._player._options.setUIVisibility(false);

      // Add crosshair to scene
      this._player.crosshair.position.z = -2;
      this._player.camera.add(this._player.crosshair);

      // Edit baseplate rotation
      this.remove(this._basePlate);
      this._basePlate = this.createBaseplate(viewerMode);
      this.add(this._basePlate);
      //this._basePlate.offsetRotation = this.rotation.y;
    } else {
      if (this._player._options.setUIVisibility)
        this._player._options.setUIVisibility(true);
      clearTimeout(this._player._clickToShowMenuTimeout);

      // Remove crosshair from scene
      this._player.camera.remove(this._player.crosshair);

      this.remove(this._basePlate);
      this._basePlate = this.createBaseplate(viewerMode);
      this.add(this._basePlate);
    }
  }

  /**
   *
   * @param ratio
   */
  handleMouseBorderRotate(ratio) {
    let rotate = (ratio - 0.5) * Math.PI * 2 + Math.PI / 2;
    this.group.rotation.y = rotate;
  }

  /**
   *
   * @param obj
   * @returns {*}
   */
  withinViewport(obj) {
    return this._player.withinViewport(obj);
  }

  /**
   *
   * @param obj
   * @returns {{x: number, y: number}}
   */
  screenXY(obj) {
    var vector = new THREE.Vector3();

    var widthHalf = 0.5 * window.innerWidth;
    var heightHalf = 0.5 * window.innerHeight;

    obj.updateMatrixWorld();
    vector.setFromMatrixPosition(obj.matrixWorld);
    vector.project(this._player.getActiveCamera());

    vector.x = vector.x * widthHalf + widthHalf;
    vector.y = -(vector.y * heightHalf) + heightHalf;

    return {
      x: vector.x,
      y: vector.y,
    };
  }

  isFullyLoaded() {
    return this.fullyLoaded;
  }

  /**
   *
   * @param setTo
   */
  setNodeVisibility(setTo) {
    this.showNodes = setTo;
  }

  /**
   *
   * @param direction
   */
  fadeHotspots(direction = "OUT") {
    if (this.split && !this.isOther) this._otherScene.fadeHotspots(direction);

    direction = direction.toUpperCase();

    let startOpacity;
    let endOpacity;
    switch (direction) {
      case "OUT":
        startOpacity = 1;
        endOpacity = 0;
        break;
      case "IN":
        startOpacity = 0;
        endOpacity = 1;
        break;
      default:
        startOpacity = 1;
        endOpacity = 0;
        break;
    }

    const hotspots = this._hotspots;

    for (const hotspot of hotspots) {
      if (hotspot.getOpacity() === endOpacity) return;
    }

    const _transitionSpeed = 750;
    // Fade hotspots out
    const opacity = { value: startOpacity };
    const opacityTween = new TWEEN.Tween(opacity).to(
      {
        value: endOpacity,
      },
      _transitionSpeed * 0.1,
    );
    opacityTween.onUpdate(() => {
      for (const hotspot of hotspots) {
        hotspot.setOpacity(opacity.value);
      }
    });
    opacityTween.start();
  }

  /**
   * Renders the scene with the given renderer and camera
   *
   * @param {THREE.Renderer} renderer
   * @param {THREE.Camera} camera
   * @param {bool} useFbo - render to the FBO
   */
  render(renderer, camera, useFbo) {
    // Early return if player has been disposed
    if (!this._player) {
      return;
    }

    this.update();

    if (this._player.crosshair) {
      this._player.crosshair.material.opacity = this._player.splashscreen
        ? 0
        : 0.5;
    }

    //Scaling dependant on the window height
    let scaleAdjust = 1.2;
    if (window.innerHeight < 400) {
      scaleAdjust = 0.8;
    }

    let zoom = this._player.controlsOrbit.object.zoom || 1;

    let scaling = (1 / zoom) * (1 / window.innerHeight) * 500 * scaleAdjust;

    if (this._player.renderer.xr.enabled) {
      scaling = (1 / zoom) * 0.7;
    }

    if (window.showSplash) {
      this.isSplashScreen = true;
    } else {
      if (this.isSplashScreen) {
        this.checkPrefetch();
        this.isSplashScreen = false;
      }
    }

    for (let i in this._hotspots) {
      if (!this._hotspots[i]?._isHover) {
        //Make link hotspots larger
        let hotspotScale = scaling;

        if (this._hotspots[i]._config?.type === "web") {
          hotspotScale = scaling * 1;
        }

        if (
          this._hotspots[i].geometry?.boundingSphere &&
          !this._player.renderer.xr.enabled
        ) {
          const hotspotXY = this.screenXY(this._hotspots[i]);
          const calcScaleFactor = (windowSize, axis, offset) => {
            // Scale Horzontally
            const norm = Math.max(
              Math.min(hotspotXY[axis] - offset, windowSize),
              0,
            );

            // A parabola with max height 1 and running through points (0,p), (k,p) and (k/2,1)
            const k = windowSize;
            const p = axis === "x" ? 0.2 : 0.4; // Increased minimum scale factor for wider visibility
            const b = (5 * (1 - p)) / k; // Reduced from 6 to 5 for gentler curve
            const a = -b / k;
            const absScale = a * Math.pow(norm, 2) + b * norm + p;
            return absScale;
          };

          const middleSquare = Math.min(window.innerWidth, window.innerHeight);

          hotspotScale =
            hotspotScale *
            Math.min(
              calcScaleFactor(
                middleSquare,
                "y",
                (window.innerHeight - middleSquare) / 2,
              ),
              calcScaleFactor(
                middleSquare,
                "x",
                (window.innerWidth - middleSquare) / 2,
              ),
            );
          // attempt at scaling based on mouse position

          // if (!this._player.isMobile) {
          //   const { x, y } = this.latestMouse;
          //   const distance = Math.sqrt(
          //     (x - hotspotXY.x) ** 2 + (y - hotspotXY.y) ** 2
          //   );
          //   hotspotScale += THREE.MathUtils.clamp(
          //     1 / distance,
          //     0,
          //     hotspotScale * 1.5
          //   );
          // }

          // Pulse animation for movement/elevation nodes when near center
          const isMovementNode = ['link', 'elevate-up', 'elevate-down', 'fade'].includes(
            this._hotspots[i]._config?.type
          );

          if (isMovementNode) {
            // Calculate distance to viewport center
            const centerX = window.innerWidth / 2;
            const centerY = window.innerHeight / 2;
            const distanceToCenter = Math.sqrt(
              Math.pow(hotspotXY.x - centerX, 2) + 
              Math.pow(hotspotXY.y - centerY, 2)
            );
            
            // Only animate if near center (within 40% of viewport)
            const maxDistance = Math.min(window.innerWidth, window.innerHeight) * 0.4;
            
            if (distanceToCenter < maxDistance) {
              // Initialize pulse phase if not exists
              if (this._hotspots[i].pulsePhase === undefined) {
                this._hotspots[i].pulsePhase = Math.random() * Math.PI * 2; // Random start phase
              }
              
              // Increment pulse phase (controls animation speed)
              this._hotspots[i].pulsePhase += 0.04; // Gentle speed
              
              // Create pulse effect using sine wave (smooth oscillation)
              // enlarge (1.15) -> normal (1.0) -> enlarge (1.15) continuously
              const pulseScale = 1.0 + (Math.sin(this._hotspots[i].pulsePhase) * 0.5 + 0.5) * 0.15;
              
              // Apply pulse to scale
              hotspotScale = hotspotScale * pulseScale;
            } else {
              // Reset pulse phase when far from center
              this._hotspots[i].pulsePhase = undefined;
            }
          }

          this._hotspots[i].scale.set(hotspotScale, hotspotScale, hotspotScale);
          this._hotspots[i].actualScale = hotspotScale;
        } else {
          this._hotspots[i].scale.set(hotspotScale, hotspotScale, hotspotScale);
          this._hotspots[i].actualScale = hotspotScale;
        }
      }

      if (!this._player.isTransitioning() && this.showNodes) {
        this._hotspots[i].visible = true;
        //if (this.getImage) {
        //  this._hotspots[i].visible = false;
        //  //renderer.setSize(1920, 1080);
        //}
      } else {
        this._hotspots[i].visible = false;
      }
    }

    //Scale the baseplate
    scaling = scaling * 0.6;
    if (this._basePlate) {
      // if ba scale more
      const { host } = window.location;
      if (host.includes("britishairways")) {
        this._basePlate.scale.set(0.5, 0.5, 0.5);
      } else {
        this._basePlate.scale.set(scaling, scaling, scaling);
      }
    } else {
      console.error("Baseplate not found");
    }

    // If using vr check if gamepad button has been pressed
    if (this._player.renderer.xr.enabled === true) {
      this.onMouseMove(
        { x: 0, y: 0 },
        { x: 0, y: 0 },
        this._player.getActiveCamera(),
      );
    }
    if (
      this._player.viewerMode === "stereo" ||
      this._player.viewerMode === "device"
    ) {
      this.onMouseMove(
        { x: 0, y: 0 },
        { x: 0, y: 0 },
        this._player.getActiveCamera(),
      );
    }

    if (this._player.viewerMode === "stereo") {
      this.effect.render(this, camera);
      return;
    }

    const width = this._player.container.clientWidth;
    const height = this._player.container.clientHeight;

    if (this.split) {
      if (this.isOther) {
        renderer.setScissor(
          width * (this._player.divider / 100),
          0,
          width,
          height,
        );
        renderer.render(this, camera);
      } else {
        renderer.setScissor(0, 0, width * (this._player.divider / 100), height);
        renderer.render(this, camera);
        this._otherScene.render(renderer, camera, useFbo);
      }
    } else {
      renderer.setScissor(0, 0, width, height);

      if (useFbo && this.fbo) {
        this._player.renderer.setRenderTarget(this.fbo);
        this._player.renderer.clear();
        this._player.renderer.render(this, camera);
        this._player.renderer.setRenderTarget(null);
      } else {
        this._player.renderer.render(this, camera);
      }
    }

    if (this.getImage && !this.isOther) {
      // Download image of the current renderer
      let data = this._player.renderer.domElement.toDataURL("image/jpeg");
      let a = this.dataURItoBlob(data);
      var url = window.URL.createObjectURL(a);
      this.download(
        `${this.imageName !== "" ? this.imageName : "export"}.jpeg`,
        url,
      );
      this.getImage = false;
      this.showNodes = true;
    }
  }

  /**
   *
   * @param timeout
   * @returns {Promise<void>}
   */
  async checkPrefetch(timeout = 500) {
    if (
      !this.prefetchTimeout &&
      !this._player?.isSplashScreen?.() &&
      !this._player?.isSlowConnection?.() &&
      this.isFullyLoaded()
    ) {
      this.prefetchTimeout = setTimeout(() => {
        let promise = Promise.resolve();
        for (let i in this._hotspots) {
          if (
            this._hotspots?.length < 3 ||
            this.withinViewport(this._hotspots[i])
          ) {
            promise.then(() => {
              return this._player.prefetchSceneFromUUID(
                this._hotspots[i]._config.options.id,
                this._hotspots[i]._config.tooltip,
              );
            });
          }
        }
        promise.then(() => {
          this.prefetchTimeout = null;
        });
      }, timeout);
    }
  }

  /**
   *
   * @param filename
   * @param data
   */
  download(filename, data) {
    var element = document.createElement("a");
    element.href = data;
    element.download = filename;

    element.style.display = "none";
    document.body.appendChild(element);

    element.click();
    document.body.removeChild(element);
  }

  /**
   *
   * @param dataURI
   * @returns {Blob}
   */
  dataURItoBlob(dataURI) {
    // convert base64 to raw binary data held in a string
    // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
    var byteString = atob(dataURI.split(",")[1]);

    // separate out the mime component
    var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

    // write the bytes of the string to an ArrayBuffer
    var ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    var ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    // write the ArrayBuffer to a blob, and you're done
    var blob = new Blob([ab], { type: mimeString });
    return blob;
  }

  /**
   * Called just before scene is removed
   * Used to cleanup.
   */
  destroy() {
    for (let h = 0; h < this._hotspots?.length; h++) {
      // Make the hotspot no longer active
      this._hotspots[h].destroy();

      // Remove the hotspot from the scene
      this.group.remove(this._hotspots[h]);
    }

    for (let v = 0; v < this._pinnedVideos?.length; v++) {
      this._pinnedVideos[v].destroy();
    }

    this._hotspots = undefined;
  }

  /**
   *
   * @param event
   * @param mouse
   * @param camera
   * @returns {boolean}
   */
  checkHoverHotspot(event, mouse, camera) {
    const hotspots = this.hotspotsUnderMouse(mouse, camera);

    if (hotspots?.length > 0) {
      return true;
    }
    return false;
  }

  /**
   * Removes hotspot from the scene
   */
  removeHotspot(hotspot) {
    const index = this._hotspots.findIndex(x => 
      x.uuid === hotspot.uuid ||
      x._config?.uuid === hotspot._config?.uuid
    );

    if (index !== -1) {
      // Make the hotspot no longer active
      this._hotspots[index].destroy();

      // Remove the hotspot from the scene
      this.group.remove(this._hotspots[index]);

      // Remove the hotspot from the array
      this._hotspots.splice(index, 1);
    }

    this._player.findTooltips();

    return index;
  }

  /**
   * Removes video from the scene
   */
  removePinnedVideo(video) {
    const index = this._pinnedVideos.findIndex(x => x.uuid === video.uuid);

    if (index !== -1) {
      // Make the video no longer active
      this._pinnedVideos[index].destroy();

      // Remove the hotspot from the scene
      this.remove(this._pinnedVideos[index]);

      // Remove the hotspot from the array
      this._pinnedVideos.splice(index, 1);
    }

    return index;
  }

  dispose() {
    clearTimeout(this.prefetchTimeout);
    clearTimeout(this._hotspotHoverTimeout);
    clearTimeout(this._player._clickToShowMenuTimeout);
    clearTimeout(this._hideTooltipTimeout);

    try {
      if (this._basePlate) {
        this._basePlate.material.dispose();
        this._basePlate.geometry.dispose();
        this.remove(this._basePlate);
        this._basePlate = undefined;
      }
      this._player.renderer.renderLists.dispose();
      // TODO: this is here because scene360 isn't currently released from memory
      // So this reduces memory where it can...


      // Dispose of all hotspots
      if (this._hotspots) {
        this._hotspots.forEach(hotspot => {
          if (hotspot.material) hotspot.material.dispose();
          if (hotspot.geometry) hotspot.geometry.dispose();
          this.group.remove(hotspot);
        });
        this._hotspots = [];
      }

      // Dispose of all pinned videos
      if (this._pinnedVideos) {
        this._pinnedVideos.forEach(video => {
          video.destroy();
          this.group.remove(video);
        });
        this._pinnedVideos = [];
      }

      // Dispose of render targets
      if (this.fbo) {
        this.fbo.dispose();
        this.fbo = null;
      }

      // Clear references
      this._player = null;
      this._image = null;
      this.group = null;
    } catch (error) {
      console.error("Error disposing of scene", error);
    }
  }
}
